import { useState, useEffect, useMemo } from "react";
import PageHeader from "../../componentes/PageHeader";
import MetaTags from "../../componentes/MetaTags";
import GradientBanner from "../../componentes/Banner";
import { FaSearch, FaRocket, FaTools, FaClock, FaStar } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LoadingSkeleton from "../../componentes/LoadingSkeleton";
import { motion } from "framer-motion";
import {
  faShieldAlt,
  faLock,
  faBug,
  faLaptopCode,
  faNetworkWired,
  faKey,
} from "@fortawesome/free-solid-svg-icons";
import { API_BASE_URL } from "../../api/config";
import "../../styles/circuit-background.css";

const baseUrl = API_BASE_URL;

const Tools = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [tools, setTools] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchBox, setShowSearchBox] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    params.append("lang", lang || "en");

    fetch(`${baseUrl}/api/tools/categories/`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setCategories(data);
        }
      })
      .catch((error) => console.error("Error fetching categories:", error));

    fetch(`${baseUrl}/api/tools?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setTools(data);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching tools:", error);
        setLoading(false);
      });
  }, [lang]);

  // Filter tools based on category and search query
  const filteredTools = tools.filter((item) => {
    const matchesCategory =
      selectedCategory === "all" ||
      (item.category && item.category.slug === selectedCategory);
    const matchesSearch =
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description &&
        item.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredTools.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTools = filteredTools.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  // Sidebar data
  const sidebarData = useMemo(() => {
    const sortedByViews = [...tools]
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .slice(0, 3);
    const recentlyUpdated = [...tools]
      .sort(
        (a, b) =>
          new Date(b.updated_at || b.created_at || 0) -
          new Date(a.updated_at || a.created_at || 0),
      )
      .slice(0, 2);
    return {
      totalTools: tools.length,
      popularTools:
        sortedByViews.length > 0 ? sortedByViews : [{ title: "N/A" }],
      recentUpdates:
        recentlyUpdated.length > 0
          ? recentlyUpdated
          : [{ title: "N/A", updated_at: "N/A" }],
    };
  }, [tools]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewTool = (toolUrl) => {
    if (toolUrl) {
      window.open(toolUrl, "_blank", "noopener,noreferrer");
    }
  };

  // Get the current category name for the title
  const currentCategoryName =
    selectedCategory === "all"
      ? t("tools.all_tools", "All Tools")
      : categories.find((cat) => cat.slug === selectedCategory)?.name ||
        "Uncategorized";

  return (
    <>
      <MetaTags
        title="Cybersecurity Tools — Ethio-CERT"
        description="Curated catalog of cybersecurity and defensive tools recommended by Ethio-CERT."
        url="https://ethiocert.gov.et/tools"
      />
      <PageHeader />
      <GradientBanner
        title={t("tools.title")}
        description={t("tools.subtitle")}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("header.tools"), link: null },
        ]}
        backgroundIcons={[
          faShieldAlt,
          faLock,
          faBug,
          faLaptopCode,
          faNetworkWired,
          faKey,
        ]}
      />
      <div className="circuit-page-background">
        <div className="flex flex-col md:flex-row gap-8 max-xl:px-4 py-4 md:py-8 px-dynamic">
          {/* Main Content */}
          <div className="flex-1">
            {/* Category Filter and Search Section */}
            <div className="flex flex-col mb-8 mt-4 text-nowrap bg-gray-100 rounded-l-lg rounded-r-lg font-bold opacity-85 text-[#20273D]">
              <div className="flex gap-4 pt-1 lg:pr-1">
                <button
                  onClick={() => {
                    setSelectedCategory("all");
                    setCurrentPage(1);
                  }}
                  className={`py-2.5 rounded-lg transition-all font-bold opacity-100 text-[#20273D] ${
                    selectedCategory === "all"
                      ? "px-3 bg-primary text-white"
                      : "bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  All Tools
                </button>
                <div
                  className="flex items-center gap-4 overflow-x-auto rounded-l-lg"
                  style={{
                    scrollbarWidth: "thin",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  {categories.map((category) => (
                    <button
                      key={category.slug}
                      onClick={() => {
                        setSelectedCategory(category.slug);
                        setCurrentPage(1);
                      }}
                      className={`px-4 h-full rounded-lg whitespace-nowrap transition-all ${
                        selectedCategory === category.slug
                          ? "bg-primary text-white"
                          : "bg-gray-100 hover:bg-gray-200"
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowSearchBox(!showSearchBox)}
                  className="rounded-r-lg p-2 ml-auto hover:bg-gray-200 transition-all"
                >
                  <FaSearch size={20} />
                </button>
              </div>
            </div>

            {/* Search Input */}
            {showSearchBox && (
              <div className="mb-6">
                <input
                  type="text"
                  placeholder={t("tools.search_placeholder", "Search tools...")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            )}

            {/* Category Title */}
            <h1 className="font-catamaran text-h-6 font-bold mb-6 text-gray-800">
              {selectedCategory === "all"
                ? t("tools.all")
                : t("common.category") + "-" + `${currentCategoryName}`}
            </h1>

            <div className="grid grid-cols-1 max-sm:gap-6 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-4">
              {loading ? (
                <LoadingSkeleton count={9} />
              ) : paginatedTools.length > 0 ? (
                paginatedTools.map((tool) => (
                  <div
                    key={tool.id}
                    className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col p-6"
                  >
                    {/* TOP ROW */}
                    <div className="flex items-start justify-between mb-4">
                      {/* Icon */}
                      {tool.image_url || tool.image_file_url ? (
                        <img
                          src={tool.image_url || tool.image_file_url}
                          alt={tool.title}
                          className="w-20 h-20 rounded-xl object-cover bg-gray-100 transition-transform duration-300 group-hover:scale-105"
                          onError={(e) => (e.target.style.display = "none")}
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-xl bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-bold text-lg">
                            T
                          </span>
                        </div>
                      )}

                      {/* Category */}
                      <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full font-medium">
                        {tool.category?.name ||
                          t("tools.uncategorized", "Uncategorized")}
                      </span>
                    </div>

                    {/* TITLE */}
                    <h3 className="font-catamaran text-base font-semibold text-gray-800 mb-2 line-clamp-2 group-hover:text-primary transition">
                      {tool.title}
                    </h3>

                    {/* DESCRIPTION */}
                    <p
                      className="text-sm text-gray-500 line-clamp-3 mb-6"
                      dangerouslySetInnerHTML={{ __html: tool.description }}
                    />

                    {/* PUSH BUTTON DOWN */}
                    <div className="mt-auto">
                      <button
                        onClick={() => handleViewTool(tool.url)}
                        disabled={!tool.url}
                        className="w-full py-2.5 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t("tools.view")}
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-600 col-span-full text-center">
                  {t("tools.no_tools_found", "No tools found.")}
                </p>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 p-2 flex justify-center items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-1 rounded-md ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-primary text-white hover:bg-primary"
                  }`}
                >
                  {t("common.previous")}
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => handlePageChange(index + 1)}
                    className={`px-4 py-1 rounded-md ${
                      currentPage === index + 1
                        ? "bg-primary text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-1 rounded-md ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-primary text-white hover:bg-primary"
                  }`}
                >
                  {t("common.next")}
                </button>
              </div>
            )}
          </div>

          {/* Enhanced Sidebar */}
          <div className="md:w-[25%] xl:block hidden">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-2xl md:p-8 relative overflow-hidden sticky top-24 max-xl:top-32"
            >
              <div className="absolute top-0 right-0 opacity-10">
                <svg
                  width="200"
                  height="200"
                  viewBox="0 0 200 200"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill="#4F46E5"
                    d="M40,0 L60,0 L100,40 L40,100 L0,60 Z"
                  />
                  <circle cx="150" cy="50" r="30" fill="#4F46E5" />
                  <rect x="120" y="100" width="60" height="60" fill="#4F46E5" />
                </svg>
              </div>

              <div className="relative z-10 border-l-4 border-primary p-4 pt-1 rounded-md mb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
                  {t("tools.insights")}
                </h2>
                <p className="text-gray-600 text-sm">
                  {t("tools.insightsubtitle")}
                </p>
              </div>

              <div className="relative z-10 space-y-6">
                <div>
                  <h3 className="font-catamaran text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
                    <FaTools className="text-primary" /> {t("tools.total")}
                  </h3>
                  <motion.p
                    whileHover={{ scale: 1.02 }}
                    className="bg-white p-3 rounded-xl shadow-md text-gray-800 font-semibold"
                  >
                    <span className="text-sm bg-primary p-1 rounded-full text-white">
                      {sidebarData.totalTools}
                    </span>{" "}
                    - {t("tools.available")}
                  </motion.p>
                </div>

                <div>
                  <h3 className="font-catamaran text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
                    <FaStar className="text-primary" /> {t("tools.popular")}
                  </h3>
                  <div className="space-y-2">
                    {sidebarData.popularTools.map((tool, index) => (
                      <motion.div
                        key={tool.title + index}
                        whileHover={{ scale: 1.02 }}
                        className="bg-white p-3 rounded-xl shadow-md flex items-center gap-2 cursor-pointer"
                        onClick={() => handleViewTool(tool.url)}
                      >
                        <span className="text-primary font-bold">
                          {index + 1}.
                        </span>
                        <span className="text-gray-800 truncate">
                          {tool.title}
                        </span>{" "}
                        -
                        <span className="p-1 bg-primary/20 rounded-xl text-xs text-primary truncate">
                          {" "}
                          {tool.category?.name ||
                            t("tools.uncategorized", "Uncategorized")}{" "}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-catamaran text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
                    <FaClock className="text-primary" /> {t("tools.recent")}
                  </h3>
                  <div className="space-y-2">
                    {sidebarData.recentUpdates.map((tool, index) => (
                      <motion.div
                        key={tool.title + index}
                        whileHover={{ scale: 1.02 }}
                        className="bg-white p-3 rounded-xl shadow-md flex flex-col gap-1 cursor-pointer"
                        onClick={() => handleViewTool(tool.url)}
                      >
                        <span className="text-gray-800 font-semibold truncate">
                          {tool.title} -
                          <span className="p-1 bg-primary/20 rounded-xl text-xs text-primary truncate">
                            {" "}
                            {tool.category?.name ||
                              t("tools.uncategorized", "Uncategorized")}{" "}
                          </span>
                        </span>
                        <span className="text-sm text-gray-600">
                          {tool.updated_at === "N/A"
                            ? "N/A"
                            : new Date(
                                tool.updated_at || tool.created_at,
                              ).toLocaleDateString()}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Tools;
